import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import {
  useAuthentication,
  useRetrieveCalendar,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { usePlanzonePermissions } from "@components/permissions";
import { EventIcon } from "@common/eventIcon";
import { TaskIcon } from "@common/taskIcon";
import { MilestoneIcon } from "@common/milestoneIcon";
import { TimeView } from "@scenes/calendar/weeklyView/timeView";
import { Icon as UnavailabilityIcon } from "@scenes/calendar/week/unavailability/icon";
import {
  utcDate,
  getMonthDates,
  getWeeks,
  padMonthWeeks,
  getMonthsTexts,
  getDaysTexts,
  sortCalendarByWeek,
  eventToCalendarEvent,
  milestoneToCalendarEvent,
  unavailabilityToCalendarEvent,
  calendarHolidaysToCalendarEvent,
  taskToCalendarEvent,
  sortEventsByLength,
  sortTasksByLength,
  sortUnavailabilitiesByLength,
  isInWeek,
  isEventInDate,
  isTaskInDate,
  isMilestoneInDate,
  isCalendarHolidaysInDate,
  isUnavailabilityInDate,
} from "@scenes/calendar/utils";
import { WeekCalendar } from "@common/weekCalendar";
import { SelectList } from "@common/selectList";
import { filter as defaultFilter } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { isTaskDone } from "../tasks/task/utils";
import { Props } from "./types";
import styles from "./styles.scss";

function getISOWeekNumber(date: Date): number {
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function getShortMonthName(months: string[], monthIndex: number): string {
  const name = months[monthIndex] || "";
  return name.substring(0, 3).toUpperCase();
}

function hashCode(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}

function intToRGB(i: number) {
  const c = (i & 0x00ffffff).toString(16).toUpperCase();
  return "00000".substring(0, 6 - c.length) + c;
}

function generateRandomColor(name: string) {
  return `#${intToRGB(hashCode(name))}`;
}

function isColorLight(color: string) {
  const hex = color.replace("#", "");
  const c_r = parseInt(hex.substring(0, 2), 16);
  const c_g = parseInt(hex.substring(2, 2 + 2), 16);
  const c_b = parseInt(hex.substring(4, 4 + 2), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
  return brightness > 155;
}

function CalendarWidget({ resource }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { planzone } = useRetrieveCurrentPlanzone();
  const userTimezone = DateTime.local().zoneName;
  const months = getMonthsTexts(t);
  const days = getDaysTexts(t);

  const now = new Date();
  const today = utcDate(now.getFullYear(), now.getMonth(), now.getDate());

  const [cursor, setCursor] = React.useState(() => today.toISOString());
  const [selectedDay, setSelectedDay] = React.useState(() =>
    today.toISOString(),
  );

  const date = new Date(cursor);
  const month = padMonthWeeks(getWeeks(getMonthDates(date)));

  const currentWeek = React.useMemo(
    () => month.find((week) => isInWeek(week, date, 6)) || month[0],
    [month, cursor],
  );

  const filter = React.useMemo(
    () => ({
      startDate: utcDate(
        currentWeek[0].getUTCFullYear(),
        currentWeek[0].getUTCMonth(),
        currentWeek[0].getUTCDate() - 1,
      ).toISOString(),
      endDate: utcDate(
        currentWeek[currentWeek.length - 1].getUTCFullYear(),
        currentWeek[currentWeek.length - 1].getUTCMonth(),
        currentWeek[currentWeek.length - 1].getUTCDate() + 1,
      ).toISOString(),
      resources: [resource],
    }),
    [currentWeek, resource],
  );

  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    calendar,
    retrieveCalendar,
  } = useRetrieveCalendar(filter);

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveCalendar({ planzone, filter });
  }, [
    filter.startDate,
    filter.endDate,
    planzone.uuid,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
  ]);

  const events = React.useMemo(
    () =>
      sortEventsByLength(
        calendar.events.filter((e) =>
          (e.participants || []).some((p) => p.uuid === resource.uuid),
        ),
      ),
    [calendar.events, resource.uuid],
  );

  const tasks = React.useMemo(
    () =>
      sortTasksByLength(
        calendar.tasks.filter((t) =>
          t.allocations.some((a) => a.resource.uuid === resource.uuid),
        ),
      ),
    [calendar.tasks, resource.uuid],
  );

  const unavailabilities = React.useMemo(
    () =>
      sortUnavailabilitiesByLength(
        calendar.unavailabilities.filter(
          (u) => u.resource.uuid === resource.uuid,
        ),
      ),
    [calendar.unavailabilities, resource.uuid],
  );

  const weekData = React.useMemo(() => {
    const calendarElements = sortCalendarByWeek(
      currentWeek,
      events,
      calendar.milestones,
      unavailabilities,
      calendar.calendarsHolidays,
      tasks,
      userTimezone,
    );

    return {
      week: currentWeek,
      events: calendarElements.events.map((event) =>
        eventToCalendarEvent(event, currentWeek, userTimezone),
      ),
      unavailabilities: calendarElements.unavailabilities.map(
        (unavailability) =>
          unavailabilityToCalendarEvent(
            unavailability,
            currentWeek,
            userTimezone,
          ),
      ),
      milestones: calendarElements.milestones.map((milestone) =>
        milestoneToCalendarEvent(milestone, currentWeek, userTimezone),
      ),
      calendarsHolidays: calendarElements.calendarHolidays.map(
        (calendarHolidays) =>
          calendarHolidaysToCalendarEvent(calendarHolidays, currentWeek),
      ),
      tasks: calendarElements.tasks.map((task) =>
        taskToCalendarEvent(task, currentWeek, userTimezone),
      ),
    };
  }, [
    currentWeek,
    events,
    tasks,
    calendar.milestones,
    unavailabilities,
    calendar.calendarsHolidays,
  ]);

  const selectedDate = new Date(selectedDay);
  const weekNumber = getISOWeekNumber(currentWeek[0]);
  const monday = currentWeek[0];
  const weekCalendarDate = {
    year: monday.getUTCFullYear(),
    month: monday.getUTCMonth(),
    day: monday.getUTCDate(),
  };

  const prevWeek = () => {
    const d = new Date(cursor);
    setCursor(
      utcDate(
        d.getUTCFullYear(),
        d.getUTCMonth(),
        d.getUTCDate() - 7,
      ).toISOString(),
    );
  };

  const nextWeek = () => {
    const d = new Date(cursor);
    setCursor(
      utcDate(
        d.getUTCFullYear(),
        d.getUTCMonth(),
        d.getUTCDate() + 7,
      ).toISOString(),
    );
  };

  const isToday = (day: Date) =>
    now.getFullYear() === day.getUTCFullYear() &&
    now.getMonth() === day.getUTCMonth() &&
    now.getDate() === day.getUTCDate();

  const isWeekend = (day: Date) =>
    day.getUTCDay() === 0 || day.getUTCDay() === 6;

  const isSelected = (day: Date) =>
    selectedDate.getUTCFullYear() === day.getUTCFullYear() &&
    selectedDate.getUTCMonth() === day.getUTCMonth() &&
    selectedDate.getUTCDate() === day.getUTCDate();

  const getDayItems = (day: Date) => {
    const items: {
      type: "event" | "task" | "milestone" | "unavailability" | "holiday";
      name: string;
      time?: string;
      data?: any;
      projectName?: string;
    }[] = [];

    calendar.calendarsHolidays.forEach((ch) => {
      if (isCalendarHolidaysInDate(day, ch)) {
        ch.holidays.forEach((holiday) => {
          const holidayDate = new Date(holiday.holidayDate);
          const roundedHoliday = utcDate(
            holidayDate.getUTCFullYear(),
            holidayDate.getUTCMonth(),
            holidayDate.getUTCDate(),
          );
          const roundedDay = utcDate(
            day.getUTCFullYear(),
            day.getUTCMonth(),
            day.getUTCDate(),
          );
          if (roundedHoliday.getTime() === roundedDay.getTime()) {
            items.push({ type: "holiday", name: holiday.name });
          }
        });
      }
    });

    unavailabilities.forEach((u) => {
      if (isUnavailabilityInDate(day, u, userTimezone)) {
        items.push({
          type: "unavailability",
          name: u.label,
        });
      }
    });

    events.forEach((e) => {
      if (isEventInDate(day, e, userTimezone)) {
        const startDate = new Date(e.startTime);
        items.push({
          type: "event",
          name: e.name,
          time: startDate.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
          }),
          projectName: e.project?.name,
          data: e,
        });
      }
    });

    calendar.milestones.forEach((m) => {
      if (isMilestoneInDate(day, m, userTimezone)) {
        items.push({ type: "milestone", name: m.name, data: m });
      }
    });

    tasks.forEach((task) => {
      if (isTaskInDate(day, task, userTimezone)) {
        const startDate = task.startDate ? new Date(task.startDate) : null;
        const isWholeDay =
          startDate &&
          startDate.getUTCHours() === 0 &&
          startDate.getUTCMinutes() === 0 &&
          startDate.getUTCSeconds() === 0;
        items.push({
          type: "task",
          name: task.name,
          time:
            startDate && !isWholeDay
              ? startDate.toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : undefined,
          projectName: task.project?.name,
          data: task,
        });
      }
    });

    return items;
  };

  const selectedDayMilestones = React.useMemo(
    () =>
      calendar.milestones.filter((m) =>
        isMilestoneInDate(selectedDate, m, userTimezone),
      ),
    [calendar.milestones, selectedDay],
  );

  const selectedDayDate = React.useMemo(() => [selectedDate], [selectedDay]);

  const dayIndex = (day: Date) => {
    const d = day.getUTCDay();
    return d === 0 ? 6 : d - 1;
  };

  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [widthPercent, setWidthPercent] = React.useState<number | null>(null);
  const canMove = React.useRef(false);

  const getPercent = (clientX: number) => {
    if (!contentRef.current) return 0;
    const rect = contentRef.current.getBoundingClientRect();
    return Math.round(((clientX - rect.left) / rect.width) * 100);
  };

  React.useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!canMove.current) return;
      const percent = getPercent(e.clientX);
      if (percent >= 0 && percent <= 100) setWidthPercent(percent);
    };
    const onMouseUp = () => {
      if (canMove.current) {
        canMove.current = false;
        document.documentElement.classList.remove(styles.unselectable);
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  });

  const handleResizeStart = () => {
    canMove.current = true;
    document.documentElement.classList.add(styles.unselectable);
  };

  return (
    <div className={styles.widget}>
      <div className={styles.header}>
        <p className={styles.monthYear}>
          {months[date.getUTCMonth()]} {date.getUTCFullYear()}
        </p>
      </div>
      <div className={styles.subHeader}>
        <SelectList<string>
          listClassName={styles.weekCalendarDropdown}
          rootClassName={styles.weekSelectorRoot}
        >
          {({ select }) => ({
            input: (
              <div className={styles.weekSelector}>
                <span
                  className={styles.arrow}
                  onClick={(e) => {
                    e.stopPropagation();
                    prevWeek();
                  }}
                >
                  &#9664;
                </span>
                <span className={styles.weekLabel}>
                  {t("calendarPage.weeklyView")} {weekNumber}
                </span>
                <span
                  className={styles.arrow}
                  onClick={(e) => {
                    e.stopPropagation();
                    nextWeek();
                  }}
                >
                  &#9654;
                </span>
              </div>
            ),
            list: (
              <WeekCalendar
                date={weekCalendarDate}
                today={() => {
                  const n = new Date();
                  const todayDate = utcDate(
                    n.getFullYear(),
                    n.getMonth(),
                    n.getDate(),
                  );
                  setCursor(select(todayDate.toISOString()));
                  setSelectedDay(todayDate.toISOString());
                }}
                editWeek={({ year, month, day }) => {
                  const nextMonday = new Date(
                    Date.UTC(year!, month!, day!),
                  ).toISOString();
                  setCursor(select(nextMonday));
                }}
              />
            ),
          })}
        </SelectList>
        <span className={styles.todayLabel}>
          {isToday(selectedDate)
            ? t("calendar.today")
            : `${days[dayIndex(selectedDate)]?.substring(0, 3).toLowerCase()}. ${selectedDate.getUTCDate()} ${getShortMonthName(months, selectedDate.getUTCMonth())}`}
        </span>
      </div>
      <div className={styles.content} ref={contentRef}>
        <div
          className={styles.dayList}
          style={
            widthPercent !== null
              ? { width: `${widthPercent}%`, flex: "none" }
              : undefined
          }
        >
          {currentWeek.map((day, i) => {
            const items = getDayItems(day);
            const maxItems = 3;
            const hasMore = items.length > maxItems;
            const visibleItems = items.slice(0, maxItems);

            return (
              <div
                key={i}
                className={`${styles.dayRow} ${isSelected(day) ? styles.dayRowSelected : ""} ${isToday(day) ? styles.dayRowToday : ""} ${isWeekend(day) ? styles.dayRowWeekend : ""} ${items.some((item) => item.type === "holiday") ? styles.dayRowHoliday : ""}`}
                onClick={() => setSelectedDay(day.toISOString())}
              >
                <div className={styles.dayInfo}>
                  <span className={styles.dayName}>
                    {days[dayIndex(day)]?.substring(0, 3).toLowerCase()}.
                  </span>
                  <span className={styles.dayNumber}>{day.getUTCDate()}</span>
                  <span className={styles.dayMonth}>
                    {getShortMonthName(months, day.getUTCMonth())}
                  </span>
                </div>
                <div className={styles.dayItems}>
                  {visibleItems.map((item, j) => {
                    if (item.type === "holiday") {
                      return (
                        <div key={j} className={styles.holidayBar}>
                          <span className={styles.holidayName}>
                            {item.name}
                          </span>
                        </div>
                      );
                    }

                    if (item.type === "unavailability") {
                      return (
                        <div key={j} className={styles.unavailabilityBar}>
                          <UnavailabilityIcon
                            className={styles.unavailabilityIcon}
                          />
                          <span className={styles.unavailabilityName}>
                            {item.name}
                          </span>
                        </div>
                      );
                    }

                    if (item.type === "milestone") {
                      return (
                        <div
                          key={j}
                          className={classNames(
                            styles.milestoneBar,
                            styles.milestoneBarClickable,
                          )}
                          onClick={() =>
                            pushView({
                              key: UseCases.retrieveMilestone,
                              data: {
                                milestone: item.data,
                                isMultiProject: true,
                              },
                            })
                          }
                        >
                          <MilestoneIcon
                            className={styles.milestoneBarIcon}
                            milestone={item.data}
                          />
                          <span className={styles.milestonBarName}>
                            {item.name}
                          </span>
                        </div>
                      );
                    }

                    const isCompleted =
                      item.type === "task" && isTaskDone(item.data);
                    const bgColor = generateRandomColor(
                      item.projectName || item.name,
                    );
                    const light = isColorLight(bgColor);
                    const textColor = light ? "#000000" : "#FFFFFF";
                    const timeColor = light ? "#909090" : "#e7e7e7";

                    return (
                      <div
                        key={j}
                        className={classNames(
                          styles.calendarBar,
                          styles.calendarBarClickable,
                        )}
                        style={{ backgroundColor: bgColor }}
                        onClick={() => {
                          if (item.type === "task") {
                            pushView({
                              key: UseCases.retrieveTask,
                              data: { task: item.data, isMultiProject: true },
                            });
                          } else if (item.type === "event") {
                            pushView({
                              key: UseCases.retrieveEvent,
                              data: { event: item.data, isMultiProject: true },
                            });
                          }
                        }}
                      >
                        {item.type === "event" && (
                          <EventIcon
                            className={styles.calendarBarIcon}
                            style={{ fill: textColor, stroke: textColor }}
                          />
                        )}
                        {item.type === "task" && (
                          <TaskIcon
                            className={classNames(
                              styles.calendarBarIcon,
                              isCompleted
                                ? styles.calendarBarIconCompleted
                                : light
                                  ? styles.calendarBarIconDark
                                  : styles.calendarBarIconLight,
                            )}
                          />
                        )}
                        {item.time && (
                          <span
                            className={styles.calendarBarTime}
                            style={{ color: timeColor }}
                          >
                            {item.time}
                          </span>
                        )}
                        <span
                          className={styles.calendarBarName}
                          style={{ color: textColor }}
                        >
                          {item.name}
                        </span>
                      </div>
                    );
                  })}
                  {hasMore && (
                    <span className={styles.dayItemMore}>
                      +{items.length - maxItems}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className={styles.resizeHandle} onMouseDown={handleResizeStart} />
        <div className={styles.rightPanel}>
          <div className={styles.milestonesSection}>
            {selectedDayMilestones.length > 0 ? (
              selectedDayMilestones.map((m) => (
                <div
                  key={m.uuid}
                  className={classNames(
                    styles.milestoneRow,
                    styles.milestoneRowClickable,
                  )}
                  onClick={() =>
                    pushView({
                      key: UseCases.retrieveMilestone,
                      data: { milestone: m, isMultiProject: true },
                    })
                  }
                >
                  <MilestoneIcon
                    className={styles.milestoneIcon}
                    milestone={m}
                  />
                  <span className={styles.milestoneName}>{m.name}</span>
                </div>
              ))
            ) : (
              <p className={styles.milestonesPlaceholder}>
                {t("personalSynthesis.milestonesList.placeholder")}
              </p>
            )}
          </div>
          <div className={styles.timeViewSection}>
            <TimeView
              week={selectedDayDate}
              events={weekData.events}
              tasks={weekData.tasks}
              unavailabilities={weekData.unavailabilities}
              filter={{ ...defaultFilter, resources: [resource] }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export { CalendarWidget };
